// ------------------------------------
// ASSET MAINTENANCE AI INTERFACES
// ------------------------------------
export interface PendingMaintenance {
    asset_id?: number;
    asset_details_id?: number;
    maintenance_type?: "service" | "repair" | "inspection" | "upgrade" | "other";
    maintenance_date?: string;
    next_due_date?: string;
    cost?: number;
    vendor?: number;
    description?: string;
    status?: string;
}

export interface PendingDeleteMaintenance {
    awaitingId?: boolean;
}
export const startCreateAssetMaintenanceFlow = (
    setPending: (data: PendingMaintenance | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPending({});
    pushAIChat("Sure. Which asset do you want to create maintenance for?");
    speak("Which asset do you want to create maintenance for?");
    window.dispatchEvent(
        new CustomEvent("ai-suggest-options", {
            detail: { type: "asset-type-name" }
        })
    );
};

export const processPendingAssetMaintenance = (
    text: string,
    pending: PendingMaintenance,
    setPending: (data: PendingMaintenance | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — ASSET (SUGGESTION HANDLED ELSEWHERE)
    if (!pending.asset_id) return;

    // STEP 2 — SERIAL NUMBER (SUGGESTION)
    if (!pending.asset_details_id) {
        pushAIChat("Please select the asset serial number.");
        speak("Please select the asset serial number.");
        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: {
                    type: "asset-serial-by-asset",
                    assetId: pending.asset_id
                }
            })
        );
        return;
    }

    // STEP 3 — MAINTENANCE TYPE (SUGGESTION)
    if (!pending.maintenance_type) {
        pushAIChat("What type of maintenance is this?");
        speak("What type of maintenance is this?");
        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "maintenance-type" }
            })
        );
        return;
    }

    // STEP 4 — MAINTENANCE DATE (TEXT)
    if (!pending.maintenance_date) {
        if (isNaN(Date.parse(text))) {
            pushAIChat("Please provide a valid maintenance date.");
            speak("Please provide a valid maintenance date.");
            return;
        }
        setPending({ ...pending, maintenance_date: text });
        pushAIChat("What is the next due date?");
        speak("What is the next due date?");
        return;
    }

    // STEP 5 — NEXT DUE DATE (TEXT)
    if (!pending.next_due_date) {
        if (isNaN(Date.parse(text))) {
            pushAIChat("Please provide a valid next due date.");
            speak("Please provide a valid next due date.");
            return;
        }
        setPending({ ...pending, next_due_date: text });
        pushAIChat("What is the maintenance cost?");
        speak("What is the maintenance cost?");
        return;
    }

    // STEP 6 — COST (TEXT)
    if (pending.cost === undefined) {
        const cost = Number(text);
        if (isNaN(cost) || cost <= 0) {
            pushAIChat("Please provide a valid maintenance cost.");
            speak("Please provide a valid maintenance cost.");
            return;
        }
        setPending({ ...pending, cost });
        pushAIChat("Please select the vendor.");
        speak("Please select the vendor.");
        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "vendor-list" }
            })
        );
        return;
    }

    // STEP 7 — VENDOR (SUGGESTION)
    if (!pending.vendor) return;

    // STEP 8 — STATUS (OPTIONAL SUGGESTION)
    if (!pending.status) {
        pushAIChat("Please select maintenance status.");
        speak("Please select maintenance status.");
        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "maintenance-status" }
            })
        );
        return;
    }

    // FINAL — SUBMIT
    const finalPayload: PendingMaintenance = {
        ...pending,
        status: pending.status || "scheduled"
    };

    pushAIChat("Creating asset maintenance.");
    speak("Creating asset maintenance.");

    window.dispatchEvent(
        new CustomEvent("ai-create-maintenance", {
            detail: finalPayload
        })
    );

    setPending(null);
};

export const startDeleteMaintenanceFlow = (
    setPendingDelete: (data: PendingDeleteMaintenance | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPendingDelete({ awaitingId: true });
    pushAIChat("Please provide the maintenance ID to delete.");
    speak("Please provide the maintenance ID.");
};
export const processPendingDeleteMaintenance = (
    text: string,
    pending: PendingDeleteMaintenance,
    setPendingDelete: (data: PendingDeleteMaintenance | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingId) return;

    const id = Number(text);
    if (isNaN(id)) {
        pushAIChat("Invalid maintenance ID.");
        speak("Invalid maintenance ID.");
        return;
    }

    pushAIChat(`Deleting maintenance ${id}...`);
    speak(`Deleting maintenance ${id}.`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-maintenance", {
            detail: { id }
        })
    );

    setPendingDelete(null);
};
